import { useEffect, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import { doctorPhotoUrl } from "./doctorConstants";
import s from "../clinic.module.css";
import u from "../../ui/ui.module.css";
import {
  Button,
  Field,
  Input,
  Textarea,
  Avatar,
  useToast,
} from "../../ui";

function Stars({ value, count }) {
  const n = Number(value) || 0;
  return (
    <div className={s.profileStars}>
      <span className={s.profileStarsValue}>
        {n > 0 ? n.toFixed(1) : "—"}
      </span>
      <span className={s.profileStarsIcons} aria-hidden>
        {[1, 2, 3, 4, 5].map((i) => (
          <span
            key={i}
            className={i <= Math.round(n) ? s.starOn : s.starOff}
          >
            ★
          </span>
        ))}
      </span>
      {count != null && (
        <span className={s.profileStarsCount}>
          {count} {count === 1 ? "отзыв" : count < 5 ? "отзыва" : "отзывов"}
        </span>
      )}
    </div>
  );
}

export default function AdminDoctorPublicProfile({
  doctor,
  edit,
  setEdit,
  onSave,
  doctorId,
  onPhotoUploaded,
}) {
  const toast = useToast();
  const [reviews, setReviews] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadReviews = () => {
    clinicAPI.getDoctorReviews(doctorId).then((r) => setReviews(r.reviews || []));
  };

  useEffect(() => {
    loadReviews();
  }, [doctorId]);

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const r = await clinicAPI.uploadDoctorPhoto(doctorId, file);
      toast.success("Фото обновлено");
      onPhotoUploaded?.(r.photo_url);
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка загрузки");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (typeof onSave === "function") await onSave();
    } finally {
      setSaving(false);
    }
  };

  const photoSrc = doctorPhotoUrl(doctor?.photo_url);
  const published = reviews.filter((r) => r.is_published);

  return (
    <div className={s.profileLayout}>
      <div className={s.profileHero}>
        <div className={s.profilePhotoWrap}>
          <Avatar name={doctor?.full_name} src={photoSrc} size="lg" />
          <label className={s.profilePhotoBtn}>
            Изменить фото
            <input type="file" accept="image/*" hidden onChange={onPhoto} />
          </label>
        </div>
        <div className={s.profileHeroBody}>
          <h3 className={s.profileHeroName}>{doctor?.full_name}</h3>
          {doctor?.specialization && (
            <p className={s.profileHeroSpec}>{doctor.specialization}</p>
          )}
          <Stars
            value={doctor?.rating_avg}
            count={doctor?.rating_count}
          />
          <p className={s.profileRatingNote}>
            Рейтинг ставят только клиенты после занятия. Админ может скрыть
            отзыв с портала, но не выставляет оценку.
          </p>
        </div>
      </div>

      <div className={s.docSection}>
        <h4 className={s.docSectionTitle}>Публичная карточка</h4>
        <div className={s.profileFormGrid}>
          <Field label="Стаж (лет)">
            <Input
              type="number"
              min={0}
              value={edit.experience_years}
              onChange={(e) =>
                setEdit({ ...edit, experience_years: e.target.value })
              }
            />
          </Field>
          <Field label="Описание для клиентов">
            <Textarea
              rows={5}
              placeholder="Образование, подход к лечению, что важно знать перед визитом…"
              value={edit.description}
              onChange={(e) =>
                setEdit({ ...edit, description: e.target.value })
              }
            />
          </Field>
        </div>
        <div className={s.scheduleActions}>
          <Button type="button" onClick={save} disabled={saving}>
            {saving ? "Сохранение…" : "Сохранить профиль"}
          </Button>
        </div>
      </div>

      <div className={s.docSection}>
        <div className={s.docSectionHead}>
          <h4 className={s.docSectionTitle}>Отзывы клиентов</h4>
          <span className={s.docTabBadge}>{published.length} на портале</span>
        </div>
        {reviews.length === 0 ? (
          <p className={s.docEmptyHint}>
            Пока нет отзывов. Они появятся, когда клиенты оценят занятие в
            личном кабинете.
          </p>
        ) : (
          <ul className={s.reviewCards}>
            {reviews.map((rev) => (
              <li
                key={rev.id}
                className={`${s.reviewCard} ${!rev.is_published ? s.reviewCardHidden : ""}`}
              >
                <div className={s.reviewCardTop}>
                  <span className={s.reviewCardStars}>★ {rev.rating}</span>
                  <span className={s.reviewCardAuthor}>
                    {rev.patient?.full_name || rev.author_name || "Клиент"}
                  </span>
                  {!rev.is_published && (
                    <span className={`${u.badge} ${u.badgeRed}`}>Скрыт</span>
                  )}
                </div>
                {rev.comment && (
                  <p className={s.reviewCardText}>{rev.comment}</p>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    await clinicAPI.patchDoctorReview(doctorId, rev.id, {
                      is_published: !rev.is_published,
                    });
                    loadReviews();
                    onPhotoUploaded?.();
                    toast.success(
                      rev.is_published ? "Отзыв скрыт" : "Отзыв на портале",
                    );
                  }}
                >
                  {rev.is_published ? "Скрыть с портала" : "Показать на портале"}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
